import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UtensilsCrossed,
  Store,
  Image as ImageIcon,
  UserCircle,
  ClipboardList,
  CheckCircle2,
  Camera,
  AlignLeft,
  Clock,
  Bell,
  ChevronRight,
  FileBadge,
  Signature,
  Circle,
} from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import { PrimaryButton } from '../shared/ui';
import { useAuth } from '../contexts/AuthContext';
import { listenToMenuItems, updateUserProfile } from '../shared/services/userService';

// Mirrors iOS ChecklistItem: icon in a 50pt circle (green when done),
// title + description, trailing chevron when incomplete.
function ChecklistItem({ icon: Icon, title, description, isCompleted, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-4 p-4 bg-white rounded-2xl shadow-sm border border-gray-100 text-left active:bg-gray-50"
    >
      <span
        className={`w-[50px] h-[50px] rounded-full flex items-center justify-center shrink-0 ${
          isCompleted ? 'bg-green-100' : 'bg-gray-100'
        }`}
      >
        {isCompleted ? (
          <CheckCircle2 size={24} className="text-green-500" />
        ) : (
          <Icon size={24} className="text-[#FF6B35]" />
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-500 mt-0.5 leading-snug">
          {description}
        </span>
      </span>
      {!isCompleted && (
        <ChevronRight size={16} className="text-gray-300 mt-4 shrink-0" />
      )}
    </button>
  );
}

// Mirrors iOS TipCard: orange icon + bold title + caption on orange tint.
function TipCard({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl bg-orange-50">
      <Icon size={20} className="text-[#FF6B35] shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5 leading-snug">
          {description}
        </p>
      </div>
    </div>
  );
}

// Mirrors iOS RestaurantWelcomeView: welcome header, getting-started
// checklist computed from live data, pro tips, Get Started / Later buttons.
export default function WelcomePage() {
  const { user, userProfile, restaurant, restaurantId } = useAuth();
  const navigate = useNavigate();
  const [menuCount, setMenuCount] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(!!userProfile?.acceptedLegalTerms);
  useEffect(() => { setLegalAccepted(!!userProfile?.acceptedLegalTerms); }, [userProfile]);

  useEffect(() => {
    if (!restaurantId) {
      setMenuCount(0);
      return;
    }
    return listenToMenuItems(
      restaurantId,
      (list) => setMenuCount(list.length),
      () => setMenuCount(0)
    );
  }, [restaurantId]);

  const checks = useMemo(() => {
    const hasCompletedProfile = !!(
      restaurant &&
      restaurant.name &&
      restaurant.address &&
      restaurant.phone
    );
    const hasBannerImage = !!restaurant?.bannerURL;
    const hasProfilePhoto = !!(
      userProfile?.profileImageURL || userProfile?.photoURL
    );
    const hasMenuItems = (menuCount ?? 0) >= 3;
    const isAcceptingOrders = !!restaurant?.isAcceptingOrders;
    return [
      {
        icon: Store,
        title: 'Complete Your Restaurant Profile',
        description:
          'Add your restaurant name, address, phone number, and operating hours',
        isCompleted: hasCompletedProfile,
        to: '/business/profile',
      },
      {
        icon: ImageIcon,
        title: 'Upload Restaurant Banner',
        description:
          'Add an attractive banner image to showcase your restaurant',
        isCompleted: hasBannerImage,
        to: '/business/profile',
      },
      {
        icon: UserCircle,
        title: 'Add Your Profile Photo',
        description:
          'Upload a professional photo so customers know who you are',
        isCompleted: hasProfilePhoto,
        to: '/business/profile',
      },
      {
        icon: ClipboardList,
        title: 'Add Menu Items',
        description:
          'Create at least 3 menu items with photos, descriptions, and prices',
        isCompleted: hasMenuItems,
        to: restaurantId ? '/business/menu' : '/business/profile',
      },
      {
        icon: FileBadge,
        title: "Upload Food Handler's Permit",
        description:
          "Submit a photo of your business's valid food handler's permit for review",
        isCompleted: !!(restaurant?.raw?.foodHandlerPermitURL),
        to: '/business/profile',
      },
      {
        icon: Signature,
        title: 'Review & Accept Legal Terms',
        description:
          'Read and agree to the Terms of Service and Liability Waiver',
        isCompleted: legalAccepted,
        to: '/business/profile',
      },
      {
        icon: CheckCircle2,
        title: 'Enable Order Acceptance',
        description:
          'Turn on your restaurant status to start receiving orders',
        isCompleted: isAcceptingOrders,
        to: '/business/orders',
      },
    ];
  }, [restaurant, userProfile, menuCount, restaurantId, legalAccepted]);

  const doneCount = checks.filter((c) => c.isCompleted).length;
  const allDone = doneCount === checks.length;
  const progress = Math.round((doneCount / checks.length) * 100);

  const finish = () => {
    try {
      localStorage.setItem('hasSeenRestaurantWelcome', 'true');
    } catch (e) {
      /* ignore */
    }
    navigate('/business/orders');
  };

  return (
    <div>
      <ChefHeader
        title="Welcome"
        right={
          <button
            type="button"
            onClick={finish}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 active:bg-gray-100"
          >
            ✕
          </button>
        }
      />

      <div className="px-4 pt-5 pb-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-3">
          <UtensilsCrossed size={72} className="text-[#FF6B35]" />
          <h1 className="text-2xl font-extrabold text-gray-900">
            Welcome to Chef!
          </h1>
          <p className="text-sm text-gray-500 max-w-[300px]">
            Let's get your restaurant set up and ready to receive orders
          </p>
        </div>

        {/* Checklist */}
        <div>
          <div className="flex items-center justify-between px-1 mb-2">
            <h2 className="text-base font-bold text-gray-900">
              Getting Started Checklist
            </h2>
            <span className="text-xs font-semibold text-gray-400">
              {doneCount}/{checks.length}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden mb-3">
            <div
              className="h-full bg-[#FF6B35] rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="space-y-3">
            {checks.map((c) => (
              <ChecklistItem
                key={c.title}
                icon={c.icon}
                title={c.title}
                description={c.description}
                isCompleted={c.isCompleted}
                onClick={() => navigate(c.to)}
              />
            ))}
          </div>
        </div>

        {/* Pro Tips */}
        <div>
          <h2 className="text-base font-bold text-gray-900 px-1 mb-2">
            Pro Tips
          </h2>
          <div className="space-y-2.5">
            <TipCard
              icon={Camera}
              title="High-Quality Photos"
              description="Use clear, well-lit photos of your food to attract more customers"
            />
            <TipCard
              icon={AlignLeft}
              title="Detailed Descriptions"
              description="Include ingredients and allergen information to help customers make informed choices"
            />
            <TipCard
              icon={Clock}
              title="Accurate Prep Times"
              description="Set realistic preparation times to ensure customer satisfaction"
            />
            <TipCard
              icon={Bell}
              title="Stay Responsive"
              description="Respond quickly to new orders to maintain high ratings"
            />
          </div>
        </div>

        {/* Legal Agreement — required before continuing */}
        <div className="space-y-2.5">
          <p className="px-1 text-base font-bold text-gray-900">Legal Agreement</p>
          <div className="bg-orange-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <a
                href="https://chefskn.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#FF6B35]"
              >
                📄 Terms of Service
              </a>
              <a
                href="https://chefskn.com/liability"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#FF6B35]"
              >
                ⚠️ Liability Waiver
              </a>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (legalAccepted || !user) return;
                setLegalAccepted(true);
                try {
                  await updateUserProfile(user.uid, { acceptedLegalTerms: true });
                } catch (e) { console.error('accept terms', e); }
              }}
              className="w-full flex items-start gap-2.5 text-left"
            >
              <span className={`mt-0.5 shrink-0 ${legalAccepted ? 'text-green-500' : 'text-gray-300'}`}>
                {legalAccepted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
              </span>
              <span className="text-xs text-gray-700">
                I have read and agree to the Terms of Service and Liability Waiver
              </span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <PrimaryButton onClick={finish} disabled={!legalAccepted}>
            {allDone ? 'Done' : 'Get Started'}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => navigate('/business/orders')}
            className="w-full py-2 text-sm text-gray-500 font-medium active:text-gray-700"
          >
            I'll Do This Later
          </button>
        </div>
      </div>
    </div>
  );
}
